import React, { useEffect, useState } from 'react';
import { Badge, Button, Drawer, List, Radio } from 'antd';
import { Candle2Icon } from 'icons';
import { useTranslations } from 'next-intl';
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params';

import { useAppSelector } from 'store';

import { IUniversitySort } from 'modules/universities';

interface IProps {
  sortUniversity?: IUniversitySort[];
}

const FilterDrawer: React.FC<IProps> = ({ sortUniversity = [] }) => {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const isLoading = useAppSelector(state => state.routeLoading.isRouteLoading);

  const yearOptions = sortUniversity.map(item => ({
    label: item.year,
    value: item.year
  }));

  const languageSet = new Set<string>();
  const studySet = new Set<string>();

  sortUniversity.forEach(item => {
    item.languages.forEach(lang => languageSet.add(lang));
    item.studies.forEach(study => studySet.add(study));
  });

  const languageOptions = Array.from(languageSet).map(lang => ({
    label: lang,
    value: lang
  }));

  const typeOptions = Array.from(studySet).map(study => ({
    label: study,
    value: study
  }));

  const [query, setQuery] = useQueryParams({
    year: withDefault(NumberParam, yearOptions[0]?.value),
    language: withDefault(StringParam, languageOptions[0]?.value),
    study: withDefault(StringParam, typeOptions[0]?.value)
  });

  const [localYear, setLocalYear] = useState<number | undefined>(query.year);
  const [localLang, setLocalLang] = useState<string | undefined>(query.language);
  const [localStudy, setLocalStudy] = useState<string | undefined>(query.study);

  const onSave = () => {
    setQuery(
      {
        year: localYear,
        language: localLang,
        study: localStudy
      },
      'push'
    );
  };

  useEffect(() => {
    if (open) {
      setLocalYear(query.year);
      setLocalLang(query.language);
      setLocalStudy(query.study);
    }
  }, [open, query]);

  useEffect(() => {
    if (!isLoading) {
      setOpen(false);
    }
  }, [isLoading]);

  return (
    <div className="block sm:hidden">
      <Button onClick={() => setOpen(true)} className="!rounded-lg !border-none !py-0">
        <Badge size="small" count={sortUniversity.length}>
          <Candle2Icon />
        </Badge>
      </Button>

      <Drawer
        title={t('Filter')}
        placement="bottom"
        onClose={() => setOpen(false)}
        open={open}
        closeIcon={false}
        className="rounded-t-xl"
        height="auto"
        styles={{
          body: { padding: '0' },
        }}
        footer={
          <Button
            loading={isLoading}
            onClick={onSave}
            className="!my-2 !w-full !rounded-lg !border-transparent !bg-green-500 !py-4 !text-white"
          >
            {t('profile.saqlash')}
          </Button>
        }
      >
        <div className="m-5 space-y-4">
          <div>
            <h1 className="text-sm text-gray-400">{t('yil')}</h1>
            <Radio.Group
              value={localYear}
              onChange={e => setLocalYear(e.target.value)}
              className="!w-full !font-normal"
            >
              <List
                dataSource={yearOptions}
                renderItem={item => (
                  <List.Item>
                    <Radio value={item.value}>{item.label}</Radio>
                  </List.Item>
                )}
              />
            </Radio.Group>
          </div>

          <div>
            <h1 className="text-sm text-gray-400">{t('TalimTili')}</h1>
            <Radio.Group
              value={localLang}
              onChange={e => setLocalLang(e.target.value)}
              className="!w-full !font-normal"
            >
              <List
                dataSource={languageOptions}
                renderItem={item => (
                  <List.Item>
                    <Radio value={item.value}>{item.label}</Radio>
                  </List.Item>
                )}
              />
            </Radio.Group>
          </div>

          <div>
            <h1 className="text-sm text-gray-400">{t('TalimShakli')}</h1>
            <Radio.Group
              value={localStudy}
              onChange={e => setLocalStudy(e.target.value)}
              className="!w-full !font-normal"
            >
              <List
                dataSource={typeOptions}
                renderItem={item => (
                  <List.Item>
                    <Radio value={item.value}>{item.label}</Radio>
                  </List.Item>
                )}
              />
            </Radio.Group>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default FilterDrawer;
